import type { ReactNode } from 'react';

import { useSiteActivationContext } from '@/components/signup/site-activation/hooks/site-activation-context';

import { AddPaymentMethodContent } from './add-payment-method-content';
import { FormWithPaymentOverlay } from './payment-details';
import { PaymentDetailsContent } from './payment-details-content';

export type SignupFormWithPaymentSwitcherProps = {
	/** Main form content (e.g. ExistingUser or NewUser). */
	children: ReactNode;
	/** Optional header shown when payment view is "site-setup" (e.g. WelcomeBackHeading). */
	header?: ReactNode;
};

/**
 * Wraps signup form content with the payment overlay flow:
 * - "site-setup": show header (if provided) and children
 * - "payment-required": full-viewport overlay with AddPaymentMethodContent
 * - "payment": overlay with PaymentDetailsContent on top of children
 */
export const SignupFormWithPaymentSwitcher = ({
	children,
	header,
}: SignupFormWithPaymentSwitcherProps) => {
	const { paymentDetailsView } = useSiteActivationContext();

	const overlayContent =
		paymentDetailsView.view === 'payment-required' ? (
			<AddPaymentMethodContent />
		) : paymentDetailsView.view === 'payment' ? (
			<PaymentDetailsContent />
		) : undefined;

	return (
		<>
			{paymentDetailsView.view === 'site-setup' && header}
			<FormWithPaymentOverlay
				paymentDetailsPage={overlayContent}
				fullViewportOverlay={paymentDetailsView.view === 'payment-required'}
			>
				{children}
			</FormWithPaymentOverlay>
		</>
	);
};
